import { introducedNextOperationIssues } from './next-operation.js';
import type { VoyageReportKind } from './schemas.js';
import { checkReportSequence, type SequenceIssue, type SequenceReport } from './sequence.js';

/**
 * Geminin zaman çizgisi ve sefer bağları (ADR 0083). Gemi fiziksel olarak tek yerdedir; sefer bu
 * dizinin ticari ve operasyonel bir kesitidir. Sıra kuralı (`sequence.ts`) ve Next Operation kilidi
 * (`next-operation.ts`) geminin silinmemiş ve iptal edilmemiş bütün raporlarına, sefere
 * bakılmadan uygulanır (ADR 0047 §2, 0057 §5, 0058 §3–4 ve 0077 §3'ü değiştirir). Saf fonksiyonlar.
 */

const issueKey = (issue: SequenceIssue) => `${issue.id}|${issue.code}`;

/**
 * Yazma anındaki denetim: adayın (yeni ya da güncellenen rapor) geminin dizisine getirdiği yeni
 * çelişkiler. Sıra kuralı "toparlanan" kipte koşar: eski bir çelişki (kural değişmeden önce sefer
 * sefer yazılmış raporlar) sonraki raporlara zincirlenmez ve yazmayı durdurmaz; adayın kendisi ya
 * da kendinden sonraki raporu bozması durdurur (ADR 0058 §4 deseni, K35).
 */
export function introducedTimelineIssues(
  others: readonly SequenceReport[],
  candidate: SequenceReport,
): SequenceIssue[] {
  const rest = others.filter((report) => report.id !== candidate.id);
  const before = new Set(checkReportSequence(rest, { recover: true }).map(issueKey));
  const sequence = checkReportSequence([...rest, candidate], { recover: true }).filter(
    (issue) => !before.has(issueKey(issue)),
  );
  return [...sequence, ...introducedNextOperationIssues(rest, candidate)];
}

/** Bağ ve öneri girdisi: raporun seferi (kendi `voyage_id`'si ya da bağı) ve iptali. */
export interface LinkableReport {
  id: string;
  kind: VoyageReportKind;
  reportedAt: string;
  /** Raporun seferi: kendi seferi ya da bağlandığı sefer; sefersizse `null`. */
  voyageId: string | null;
  /** Gerekçeyle iptal edilmiş (ADR 0077 §3): hesaplara, öneriye ve aralığa girmez. */
  voided?: boolean;
}

/** Zaman, sonra kimlik (UUIDv7 oluşturma sırası) sırası: listelerle aynı. */
function byTime<T extends { reportedAt: string; id: string }>(reports: readonly T[]): T[] {
  return [...reports].sort(
    (a, b) =>
      Date.parse(a.reportedAt) - Date.parse(b.reportedAt) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/**
 * Sefer açılınca bağlanmak üzere önerilen sefersiz raporlar (sefer-3, sefer-5; ADR 0083 §3), tarih
 * sırasıyla. Başlangıç: seferin ilk raporu; raporu yoksa verilen başlangıç raporu; o da yoksa
 * geminin sefersiz kuyruğundaki (son seferli rapordan sonraki) ilk Departure, yoksa kuyruğun başı.
 * Bitiş: başlangıçtan sonraki ilk başka sefere bağlı rapor (yoksa bugün). İptal edilmiş rapor
 * önerilmez. Kullanıcı onaylamadan bağlanmaz; öneri yalnız seçimin varsayılanıdır.
 */
export function suggestReportLinks(
  reports: readonly LinkableReport[],
  voyageId: string,
  options: { startReportId?: string | null } = {},
): string[] {
  const live = byTime(reports.filter((report) => !report.voided));
  const own = live.filter((report) => report.voyageId === voyageId);
  let startIndex: number;
  if (own.length > 0) {
    startIndex = live.indexOf(own[0] as LinkableReport);
  } else if (options.startReportId) {
    startIndex = live.findIndex((report) => report.id === options.startReportId);
  } else {
    let lastAssigned = -1;
    live.forEach((report, index) => {
      if (report.voyageId !== null) lastAssigned = index;
    });
    const tail = live.slice(lastAssigned + 1);
    const departure = tail.findIndex((report) => report.kind === 'departure');
    startIndex = tail.length === 0 ? -1 : lastAssigned + 1 + Math.max(departure, 0);
  }
  if (startIndex < 0) return [];
  const out: string[] = [];
  for (const report of live.slice(startIndex)) {
    if (report.voyageId !== null && report.voyageId !== voyageId) break;
    if (report.voyageId === null) out.push(report.id);
  }
  return out;
}

/**
 * Seferin önünde kalan sefersiz raporlar (ADR 0157 §4, #2268): seferin ilk kendi raporundan
 * hemen önceki sefersiz dizi, aradaki ilk Departure'dan başlayarak. Başka sefere bağlı rapor diziyi
 * keser; iptal edilmiş rapor atlanır; taslaklar sayılır (çağıran verir). Seferin raporu yoksa ya da
 * aralıkta kalkış yoksa boş. Önerinin (`suggestReportLinks`) seferin ilk raporundan başlayan
 * penceresine eklenir: kalkış ve COSP sefersiz girilip sefere önce Noon yazıldıysa da bulunur.
 */
export function headLinkCandidates(reports: readonly LinkableReport[], voyageId: string): string[] {
  const live = byTime(reports.filter((report) => !report.voided));
  const first = live.findIndex((report) => report.voyageId === voyageId);
  if (first <= 0) return [];
  let start = first;
  while (start > 0 && live[start - 1]?.voyageId === null) start -= 1;
  const window = live.slice(start, first);
  const departure = window.findIndex((report) => report.kind === 'departure');
  return departure < 0 ? [] : window.slice(departure).map((report) => report.id);
}

export interface VoyageOverlap {
  /** Aralığı sonra başlayan sefer. */
  voyageId: string;
  otherVoyageId: string;
}

/**
 * Seferlerin rapor aralıkları ([ilk, son] rapor zamanı) zamanda örtüşmez (ADR 0083 §2): gemi aynı
 * anda iki seferde olamaz. Uç uca değmek (bir seferin varışı ile ötekinin kalkışı aynı an) serbest.
 * İptal edilmiş rapor aralığa girmez.
 */
export function voyageOverlaps(reports: readonly LinkableReport[]): VoyageOverlap[] {
  const spans = new Map<string, { start: number; end: number }>();
  for (const report of reports) {
    if (report.voided || report.voyageId === null) continue;
    const time = Date.parse(report.reportedAt);
    const span = spans.get(report.voyageId);
    spans.set(
      report.voyageId,
      span
        ? { start: Math.min(span.start, time), end: Math.max(span.end, time) }
        : { start: time, end: time },
    );
  }
  const list = [...spans.entries()].sort(
    ([a, x], [b, y]) => x.start - y.start || (a < b ? -1 : a > b ? 1 : 0),
  );
  const out: VoyageOverlap[] = [];
  list.forEach(([later, span], index) => {
    for (const [earlier, other] of list.slice(0, index)) {
      if (other.start < span.end && span.start < other.end) {
        out.push({ voyageId: later, otherVoyageId: earlier });
      }
    }
  });
  return out;
}

/** Değişikliğin getirdiği yeni örtüşmeler; eskiden var olan örtüşme yazmayı durdurmaz (K35). */
export function introducedOverlaps(
  before: readonly LinkableReport[],
  after: readonly LinkableReport[],
): VoyageOverlap[] {
  const key = (o: VoyageOverlap) => [o.voyageId, o.otherVoyageId].sort().join('|');
  const old = new Set(voyageOverlaps(before).map(key));
  return voyageOverlaps(after).filter((overlap) => !old.has(key(overlap)));
}
